// @ts-strict-ignore
import type { NavLinkKey } from "./constants";
import { NAV_LINK_KEYS } from "./constants";
import type { Locale } from "i18n/constants";
import type { Alpha2Code } from "i18n-iso-countries";
import { EEA_COUNTRIES } from "constants/countries";
import type { ClientUseTranslations } from "i18n/useTranslations";

export type NavLinkProps = {
  url: string;
  name: string;
  key: NavLinkKey;
  hasMenu: boolean;
  prefetch: boolean;
  id?: string; // Added for experiment tracking
};

export type Region = "US" | "UK" | "SG" | "EU" | "AU" | undefined;

interface BaseNavItem {
  id: string; // Unique identifier
  i18nKey: string; // Translation key
  url: string; // Link URL
  key: NavLinkKey; // Navigation key
  hasMenu: boolean; // Has dropdown menu
  prefetch: boolean; // Whether to prefetch

  // Region availability configuration
  availability?: {
    include?: Exclude<Region, undefined>[]; // Only in these regions
    exclude?: Exclude<Region, undefined>[]; // Not in these regions
    strictInclude?: boolean; // If true, ONLY show in specified regions, hide for undefined regions
  };
}

const NAV_ITEMS: BaseNavItem[] = [
  // Prices - available everywhere
  {
    id: "prices",
    i18nKey: "prices",
    url: "/prices",
    key: NAV_LINK_KEYS.Prices,
    hasMenu: false,
    prefetch: true
  },

  // Staking - UK only
  {
    id: "staking",
    i18nKey: "staking",
    url: "/staking",
    key: NAV_LINK_KEYS.Staking,
    hasMenu: false,
    prefetch: true,
    availability: {
      include: ["UK"],
      exclude: ["AU"]
    }
  },

  // Credit Card - US only
  {
    id: "creditCard",
    i18nKey: "creditCard",
    url: "/credit-card",
    key: NAV_LINK_KEYS.CreditCard,
    hasMenu: true,
    prefetch: true,
    availability: {
      include: ["US"],
      strictInclude: true // Only show in US, hide for undefined regions too
    }
  },

  // Perpetuals - EU and OTHER regions
  {
    id: "perpetuals",
    i18nKey: "perpetualsLink",
    url: "/perpetuals",
    key: NAV_LINK_KEYS.Perpetuals,
    hasMenu: false,
    prefetch: true,
    availability: {
      include: ["EU"],
      exclude: ["UK", "SG", "AU"]
    }
  },

  // Products - available everywhere
  {
    id: "products",
    i18nKey: "allProducts",
    url: "/",
    key: NAV_LINK_KEYS.Products,
    hasMenu: true,
    prefetch: true
  },

  // Institutions - available everywhere
  {
    id: "institutions",
    i18nKey: "institutions",
    url: "/institutions",
    key: NAV_LINK_KEYS.Institutions,
    hasMenu: true,
    prefetch: false
  },

  // Resources - available everywhere
  {
    id: "resources",
    i18nKey: "resources",
    url: "/",
    key: NAV_LINK_KEYS.Resources,
    hasMenu: true,
    prefetch: true
  }
];

export const determineRegion = (
  locale?: Locale,
  countryCode?: Alpha2Code
): Region => {
  let region: Region;

  // Country code has higher priority than locale
  if (countryCode) {
    // Australian region
    if (countryCode === "AU") region = "AU";
    // UK region
    else if (countryCode === "GB") region = "UK";
    // Singapore region
    else if (countryCode === "SG") region = "SG";
    // US region
    else if (countryCode === "US") region = "US";
    // EU region (including IE)
    else if (EEA_COUNTRIES.has(countryCode)) {
      region = "EU";
    }
    // Any other country code that doesn't match above
    else {
      region = undefined;
    }
  }
  // Fall back to locale if no country code
  else if (locale) {
    if (locale === "en-AU") region = "AU";
    else if (locale === "en-GB") region = "UK";
    else if (locale === "en-SG") region = "SG";
    else if (locale === "en-US") region = "US";
    else region = undefined;
  }
  // No locale or country code
  else {
    region = undefined;
  }

  return region;
};

export const isAvailableInRegion = (
  item: NavItem,
  region?: Region
): boolean => {
  if (!item.availability) {
    return true;
  }

  const { include, exclude, strictInclude } = item.availability;

  // If region is undefined, show all items except those with specific includes
  if (!region) {
    // strictInclude: if true, ONLY show in specified regions, hide for undefined regions
    if (strictInclude === true) {
      return false;
    }

    // If include is specified, hide the item for undefined regions
    if (include?.length > 0) {
      return false;
    }

    return true;
  }

  // If include is specified, the region must be in the include list
  if (include?.length && !include.includes(region)) {
    return false;
  }

  // If exclude is specified, the region must not be in the exclude list
  if (exclude?.length && exclude.includes(region)) {
    return false;
  }

  // If we pass all checks, the item is available
  return true;
};

const createNavLink = (
  item: BaseNavItem,
  t: ClientUseTranslations
): NavLinkProps => {
  return {
    url: item.url,
    name: t(item.i18nKey),
    key: item.key,
    hasMenu: item.hasMenu,
    prefetch: item.prefetch,
    id: item.id
  };
};

export function getMainNavigationLinks({
  t,
  locale,
  countryCode
}: {
  t: ClientUseTranslations;
  locale?: Locale;
  countryCode?: Alpha2Code;
}): NavLinkProps[] {
  const region = determineRegion(locale, countryCode);

  const filteredItems = NAV_ITEMS.filter((item) => {
    return isAvailableInRegion(item as NavItem, region);
  });

  return filteredItems.map((item) => createNavLink(item, t));
}

export type MobileSubNavProps = {
  title: string;
  items: Array<{
    url?: string;
    name?: string;
    newTab?: boolean;
    title?: string;
    useReferrerPolicy?: boolean;
  }>;
};

export interface NavItem {
  id: string;
  i18nKey?: string; // Translation key for name/title
  url?: string;
  title?: string; // Title for display (set during processing)
  titleKey?: string; // Translation key for title (headings)
  description?: string; // Description for submenu displays
  newTab?: boolean;
  useReferrerPolicy?: boolean;
  isHeading?: boolean; // Whether this is a heading item
  style?: React.CSSProperties; // Custom styling for this item
  columnIndex?: number; // Optional column index for multi-column layouts

  // Link configuration for NavLink component
  link?: {
    href: string;
    newTab?: boolean;
    useReferrerPolicy?: boolean;
    id?: string;
  };

  // Region availability configuration
  availability?: {
    include?: Region[]; // Only show in these regions
    exclude?: Region[]; // Don't show in these regions
    strictInclude?: boolean; // If true, ONLY show in specified regions, hide for undefined regions
  };
}

const CREDIT_CARD_NAV_ITEMS: NavItem[] = [
  {
    id: "business-credit-card",
    i18nKey: "businessCreditCard",
    url: "/credit-card/business",
    description: "businessCreditCardDescription",
    columnIndex: 0
  },
  {
    id: "bitcoin-credit-card-treatment-credit-card-submenu-experiment",
    i18nKey: "bitcoinCreditCard",
    url: "/credit-card/btc",
    description: "bitcoinCreditCardDescription",
    columnIndex: 0
  },
  {
    id: "xrp-credit-card-treatment-credit-card-submenu-experiment",
    i18nKey: "xrpCreditCard",
    url: "/xrp",
    description: "xrpCreditCardDescription",
    columnIndex: 1
  },
  {
    id: "solana-credit-card-treatment-credit-card-submenu-experiment",
    i18nKey: "solanaCreditCard",
    url: "/credit-card/solana",
    description: "solanaCreditCardDescription",
    columnIndex: 1
  }
];

const RESOURCES_NAV_ITEMS: NavItem[] = [
  {
    id: "blog",
    i18nKey: "blog",
    url: "/blog",
    // Common fields for both nav systems
    description: "resourcesSubMenu.blogDesc"
  },
  {
    id: "trust-center",
    i18nKey: "trustCenter",
    url: "/trust-center",
    description: "resourcesSubMenu.trustCenterDesc",
    availability: {
      exclude: ["US"]
    }
  },
  {
    id: "about",
    i18nKey: "about",
    url: "/about",
    description: "resourcesSubMenu.aboutDesc"
  },
  {
    id: "careers",
    i18nKey: "careers",
    url: "/careers",
    description: "resourcesSubMenu.careersDesc"
  },
  {
    id: "cryptopedia",
    i18nKey: "cryptopedia",
    url: "/cryptopedia",
    newTab: true,
    description: "resourcesSubMenu.cryptopediaDesc"
  },
  {
    id: "support",
    i18nKey: "support",
    url: "https://support.gemini.com/",
    newTab: true,
    description: "resourcesSubMenu.supportDesc"
  },
  {
    id: "privacy-policy",
    i18nKey: "privacyPolicy",
    url: "/legal/privacy-policy",
    newTab: true,
    description: "resourcesSubMenu.privacyPolicyDesc",
    availability: {
      include: ["AU"]
    }
  }
];

// Unified Products Navigation with region-based filtering
const PRODUCTS_NAV_ITEMS: NavItem[] = [
  // Common products available everywhere
  {
    id: "exchange",
    i18nKey: "exchange",
    url: "/exchange",
    description: "navExchangeDescription"
  },
  {
    id: "dollar",
    i18nKey: "geminiDollar",
    url: "/dollar",
    description: "geminiDollarDescription",
    availability: {
      exclude: ["AU"]
    }
  },

  // Region-specific products
  {
    id: "credit-card",
    i18nKey: "creditCard",
    url: "/credit-card",
    description: "creditCardDescription",
    availability: {
      include: ["US"],
      strictInclude: true // Only show in US, hide for undefined regions too
    }
  },
  {
    id: "activetrader",
    i18nKey: "activeTrader",
    url: "/activetrader",
    description: "activeTraderDescription",
    availability: {
      exclude: ["US", "UK", "SG"]
    }
  },
  {
    id: "perpetuals",
    i18nKey: "perpetualsLink",
    url: "/perpetuals",
    description: "perpetualsProductText",
    availability: {
      include: ["EU"],
      exclude: ["UK", "SG", "AU"]
    }
  },
  {
    id: "tokenizedstocks",
    i18nKey: "tokenizedStocks",
    url: "/tokenizedstocks",
    description: "tokenizedStocksDescription",
    availability: {
      exclude: ["US"]
    }
  },
  {
    id: "staking",
    i18nKey: "staking",
    url: "/staking",
    description: "stakingDescription",
    availability: {
      exclude: ["SG", "AU"]
    }
  },
  {
    id: "wallet",
    i18nKey: "wallet",
    url: "/wallet",
    description: "walletDescription",
    availability: {
      include: ["US", "EU"]
    }
  }
];

// Institutions Navigation Items
const INSTITUTIONS_NAV_ITEMS: NavItem[] = [
  // Institutional Products section - Column 0
  {
    id: "explore",
    i18nKey: "institutionsSubMenu.exploreGeminiInst",
    url: "/institutions",
    description: "institutionsSubMenu.cardBody",
    columnIndex: 0
  },
  {
    id: "exchange",
    i18nKey: "institutionsSubMenu.geminieExchange",
    url: "/institutions/exchange",
    description: "institutionsSubMenu.geminieExchange_desc",
    columnIndex: 0
  },
  {
    id: "custody",
    i18nKey: "institutionsSubMenu.geminiCustody",
    url: "/institutions/custody",
    description: "institutionsSubMenu.geminiCustody_desc",
    columnIndex: 0
  },
  {
    id: "otc",
    i18nKey: "institutionsSubMenu.geminiotc",
    url: "/institutions/otc",
    description: "institutionsSubMenu.geminieotc_desc",
    availability: {
      exclude: ["US"]
    },
    columnIndex: 0
  },
  {
    id: "otc-us",
    i18nKey: "institutionsSubMenu.geminiotc",
    url: "/institutions/otc",
    description: "institutionsSubMenu.geminieotc_desc",
    availability: {
      include: ["US"]
    },
    // Must be in column 1 for US regions
    columnIndex: 1
  },
  // US-specific staking menu item
  {
    id: "staking-us",
    i18nKey: "institutionsSubMenu.geminiStaking",
    url: "/staking",
    description: "stakingDescription",
    availability: {
      include: ["US"] // Only for US region
    },
    columnIndex: 1 // Column 1 for US
  },
  // Non-US staking menu item
  {
    id: "staking",
    i18nKey: "institutionsSubMenu.geminiStaking",
    url: "/staking",
    description: "stakingDescription",
    availability: {
      exclude: ["US", "SG"]
    },
    columnIndex: 0 // Column 0 for non-US
  },
  // Who we serve section - Column 1 for non-US regions only
  {
    id: "businesses",
    i18nKey: "institutionsSubMenu.businesses",
    url: "/institutions/business",
    availability: {
      exclude: ["US"]
    },
    columnIndex: 1 // Column 1 for non-US regions
  },
  {
    id: "professional-traders",
    i18nKey: "institutionsSubMenu.professionalTraders",
    url: "/institutions/professional-traders",
    availability: {
      exclude: ["US"]
    },
    columnIndex: 1 // Column 1 for non-US regions
  },
  {
    id: "institutional-investors",
    i18nKey: "institutionsSubMenu.institutionalInvestors",
    url: "/institutions/institutional-investors",
    availability: {
      exclude: ["US"]
    },
    columnIndex: 1 // Column 1 for non-US regions
  },
  {
    id: "fintechs-banks",
    i18nKey: "institutionsSubMenu.fintechsAndBanks",
    url: "/institutions/fintechs-banks",
    availability: {
      exclude: ["US"]
    },
    columnIndex: 1 // Column 1 for non-US regions
  },
  {
    id: "bitcoin-miners",
    i18nKey: "institutionsSubMenu.bitcoinMiners",
    url: "/institutions/bitcoin-miners",
    availability: {
      exclude: ["US"]
    },
    columnIndex: 1 // Column 1 for non-US regions
  }
];

const createMobileNavItems = (
  items: NavItem[],
  t: ClientUseTranslations,
  region?: Region
) => {
  return items
    .filter((item) => {
      if (region) {
        return isAvailableInRegion(item, region);
      }
      return true;
    })
    .map((item) => {
      // For header items, we want to use the titleKey for the title property
      if (item.isHeading) {
        return {
          title: item.titleKey ? t(item.titleKey) : undefined
        };
      }

      // For regular items
      return {
        url: item.url,
        name: item.i18nKey ? t(item.i18nKey) : undefined,
        newTab: item.newTab,
        title: item.titleKey ? t(item.titleKey) : undefined,
        useReferrerPolicy: item.useReferrerPolicy
      };
    });
};

export const getMobileResourcesLinks = ({
  t,
  locale,
  countryCode
}: {
  t: ClientUseTranslations;
  locale?: Locale;
  countryCode?: Alpha2Code;
}): MobileSubNavProps => {
  const region = determineRegion(locale, countryCode);

  // Filter resources items by region availability
  const filteredResourcesItems = RESOURCES_NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item, region)
  );

  return {
    title: t("resources"),
    items: createMobileNavItems(filteredResourcesItems, t, region)
  };
};

export const getMobileProductsLinks = ({
  t,
  locale,
  countryCode
}: {
  t: ClientUseTranslations;
  locale?: Locale;
  countryCode?: Alpha2Code;
}): MobileSubNavProps => {
  const region = determineRegion(locale, countryCode);

  // Get main nav items to avoid duplicates
  const mainNavItems = NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item as NavItem, region)
  );
  const mainNavItemIds = mainNavItems.map((item) => item.id);

  // Filter out items that already appear in the main navigation or don't match region restrictions
  const filteredProductsItems = PRODUCTS_NAV_ITEMS.filter((item) => {
    // Skip items that already appear in main navigation
    if (mainNavItemIds.includes(item.id)) {
      return false;
    }

    // Apply region restrictions
    return isAvailableInRegion(item, region);
  });

  return {
    title: t("productsTitle"),
    items: createMobileNavItems(filteredProductsItems, t, region)
  };
};

export const getMobileLinksByRegion = ({
  t,
  locale,
  countryCode
}: {
  t: ClientUseTranslations;
  locale?: Locale;
  countryCode?: Alpha2Code;
}) => {
  const region = determineRegion(locale, countryCode);

  // Get main nav items to avoid duplicates
  const mainNavItems = NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item as NavItem, region)
  );
  const mainNavItemIds = mainNavItems.map((item) => item.id);

  const filteredCreditCardItems = CREDIT_CARD_NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item, region)
  );

  // Filter out items that already appear in the main navigation or don't match region restrictions
  const filteredProductsItems = PRODUCTS_NAV_ITEMS.filter((item) => {
    // Skip items that already appear in main navigation
    if (mainNavItemIds.includes(item.id)) {
      return false;
    }

    return isAvailableInRegion(item, region);
  });

  const filteredResourcesItems = RESOURCES_NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item, region)
  );

  const filteredInstitutionsItems = INSTITUTIONS_NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item, region)
  );

  const isUS = region === "US";

  const institutionalProductsHeader = {
    id: "institutional-products-heading",
    titleKey: "institutionsSubMenu.institutionalProductsHeading",
    isHeading: true
  };

  const whoWeServeHeader = {
    id: "who-we-serve-heading",
    titleKey: "institutionsSubMenu.whoWeServeHeading",
    isHeading: true
  };

  const institutionsWithHeaders = [...filteredInstitutionsItems];

  institutionsWithHeaders.unshift(institutionalProductsHeader as NavItem);

  if (!isUS) {
    const firstCol1ItemIndex = institutionsWithHeaders.findIndex(
      (item) => item.columnIndex === 1
    );
    if (firstCol1ItemIndex !== -1) {
      institutionsWithHeaders.splice(
        firstCol1ItemIndex,
        0,
        whoWeServeHeader as NavItem
      );
    }
  }

  return {
    creditCard: {
      title: t("creditCard"),
      items: createMobileNavItems(filteredCreditCardItems, t, region)
    },
    resources: {
      title: t("resources"),
      items: createMobileNavItems(filteredResourcesItems, t, region)
    },
    products: {
      title: t("productsTitle"),
      items: createMobileNavItems(filteredProductsItems, t, region)
    },
    institutions: {
      title: t("institutions"),
      items: createMobileNavItems(institutionsWithHeaders, t, region)
    }
  };
};

export const getMobileInstitutionsProducts = ({
  t,
  locale,
  countryCode
}: {
  t: ClientUseTranslations;
  locale?: Locale;
  countryCode?: Alpha2Code;
}): MobileSubNavProps => {
  const region = determineRegion(locale, countryCode);
  const isUS = region === "US";

  const filteredInstitutionsItems = INSTITUTIONS_NAV_ITEMS.filter((item) =>
    isAvailableInRegion(item, region)
  );

  const institutionalProductsHeader = {
    id: "institutional-products-heading",
    titleKey: "institutionsSubMenu.institutionalProductsHeading",
    isHeading: true
  };

  const whoWeServeHeader = {
    id: "who-we-serve-heading",
    titleKey: "institutionsSubMenu.whoWeServeHeading",
    isHeading: true
  };

  const itemsWithHeaders = [...filteredInstitutionsItems];

  itemsWithHeaders.unshift(institutionalProductsHeader as NavItem);

  if (!isUS) {
    const firstCol1ItemIndex = itemsWithHeaders.findIndex(
      (item) => item.columnIndex === 1
    );
    if (firstCol1ItemIndex !== -1) {
      itemsWithHeaders.splice(
        firstCol1ItemIndex,
        0,
        whoWeServeHeader as NavItem
      );
    }
  }

  return {
    title: t("institutions"),
    items: createMobileNavItems(itemsWithHeaders, t, region)
  };
};

export type SubMenuItem = NavItem;

export interface SubMenuConfig {
  title: string;
  description?: string;
  imageSrc?: string;
  theme: "light" | "dark"; // All menus need a theme
  imagePath?: string;
  items: NavItem[];
  // Number of columns to display items in
  columnCount?: number;
  // Whether to use specified column indices (true) or auto-distribute items (false)
  useSpecificColumns?: boolean;
  // Column configuration for items with headers
  columnConfig?: {
    [columnIndex: number]: {
      header?: string; // Translation key for column header
      width?: string; // Optional CSS width for this column
    };
  };
  // Custom font size for this menu's items
  customFontSize?: string;
  // Custom line height for this menu's items
  customLineHeight?: string;
}

function processNavItems(items: NavItem[]): NavItem[] {
  return items.map((item) => {
    // Make a copy of the item
    const processedItem = { ...item };

    // For heading items (with titleKey but no i18nKey)
    if (item.titleKey && !item.i18nKey) {
      processedItem.isHeading = true;
      // Headings should have minimal margins
      processedItem.style = item.style || { margin: "16px 0 0 0" };
      // Use titleKey as the title for headings
      processedItem.title = item.titleKey;
    } else {
      // For regular items, set title from i18nKey and add default style
      if (item.i18nKey) {
        processedItem.title = item.i18nKey;
      }
      processedItem.style = processedItem.style || {
        minHeight: "70px",
        margin: "0px 0px 24px 0px"
      };

      // Add link property if url exists
      if (item.url) {
        processedItem.link = {
          href: item.url,
          newTab: item.newTab,
          useReferrerPolicy: item.useReferrerPolicy,
          id: item.id
        };
      }
    }

    return processedItem;
  });
}

const PRODUCT_ITEMS_ORDER = [
  "exchange",
  "perpetuals",
  "tokenizedstocks",
  "dollar",
  "activetrader",
  "staking",
  "credit-card",
  "wallet"
];

const RESOURCES_ITEMS_ORDER = [
  "trust-center",
  "about",
  "careers",
  "cryptopedia",
  "support",
  "privacy-policy"
];

const INSTITUTIONS_US_ITEMS_ORDER = [
  "exchange",
  "custody",
  "otc-us",
  "staking-us"
];

const INSTITUTIONS_INTL_ITEMS_ORDER = [
  "exchange",
  "custody",
  "otc",
  "staking",
  "businesses",
  "professional-traders",
  "institutional-investors",
  "fintechs-banks",
  "bitcoin-miners"
];

export function getSubMenuConfig({
  menuName,
  locale,
  countryCode
}: {
  menuName: string;
  t: ClientUseTranslations;
  locale?: Locale;
  countryCode?: Alpha2Code;
  maxStakingRewardRate?: string;
}): SubMenuConfig | null {
  const region = determineRegion(locale, countryCode);
  const isUS = region === "US";

  const getOrderedItems = (items: NavItem[], order: string[]): NavItem[] => {
    const itemMap = new Map(items.map((item) => [item.id, item]));
    return order
      .map((id) => itemMap.get(id))
      .filter(Boolean)
      .filter((item) => isAvailableInRegion(item as NavItem, region))
      .map((item) => ({ ...item }));
  };

  switch (menuName) {
    case "CreditCard":
      return {
        title: "creditCard",
        description: "creditCardDescription",
        imagePath: "/credit-card",
        imageSrc: "/images/credit_card/cc_nav_thumbnail.png",
        theme: "dark",
        items: processNavItems(CREDIT_CARD_NAV_ITEMS),
        columnCount: 2,
        useSpecificColumns: true
      };

    case "Products":
      return {
        title: "activeTrader",
        description: "activeTraderDescription",
        imagePath: "/activetrader",
        imageSrc: "/images/derivatives/derivativesBg.png",
        theme: "dark",
        items: processNavItems(
          getOrderedItems(PRODUCTS_NAV_ITEMS, PRODUCT_ITEMS_ORDER)
        ),
        columnCount: 2
      };

    case "Resources":
      return {
        title: "geminiBlog",
        description: "geminiBlogDescription",
        imagePath: "/blog",
        imageSrc: "/images/resourceBg.png",
        theme: "light",
        items: processNavItems(
          getOrderedItems(RESOURCES_NAV_ITEMS, RESOURCES_ITEMS_ORDER)
        ),
        columnCount: 2
      };

    case "Institutions":
      // For US region, show only Institutional Products with 1 column
      // For other regions, show both Institutional Products and Who We Serve with 2 columns
      return {
        title: "institutionsSubMenu.geminiInst",
        description: "institutionsSubMenu.cardBody",
        imagePath: "/institutions",
        imageSrc: "/images/institutionsMenu.png",
        theme: "dark",
        items: processNavItems(
          // Get items based on region
          getOrderedItems(
            INSTITUTIONS_NAV_ITEMS,
            isUS ? INSTITUTIONS_US_ITEMS_ORDER : INSTITUTIONS_INTL_ITEMS_ORDER
          )
        ),
        // Use 2 columns for all regions
        columnCount: 2,
        useSpecificColumns: true, // Use specific columns for all regions
        columnConfig: isUS
          ? {
              // US region: Two columns but only one header for the first column
              0: {
                header: "institutionsSubMenu.institutionalProductsHeading"
              },
              // No header for second column in US region
              1: {}
            }
          : {
              // Non-US regions: Two columns with headers
              0: {
                header: "institutionsSubMenu.institutionalProductsHeading"
              },
              1: {
                header: "institutionsSubMenu.whoWeServeHeading"
              }
            }
      };

    default:
      return null;
  }
}
